import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from 'components/AppIcon';
import { Flex, Button, Input } from 'antd';
const CAPTCHA_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function createCaptchaCode(length) {
    const values = new Uint32Array(length);

    if (globalThis.crypto?.getRandomValues) {
        globalThis.crypto.getRandomValues(values);
    } else {
        for (let index = 0; index < length; index += 1) {
            values[index] = Math.floor(Math.random() * CAPTCHA_ALPHABET.length);
        }
    }

    return Array.from(values, (value) => CAPTCHA_ALPHABET[value % CAPTCHA_ALPHABET.length]).join('');
}

function drawCaptcha(canvas, code) {
    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#f8fafc';
    context.fillRect(0, 0, canvas.width, canvas.height);

    for (let index = 0; index < 6; index += 1) {
        context.beginPath();
        context.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        context.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        context.strokeStyle = 'rgba(100, 116, 139, 0.35)';
        context.stroke();
    }

    context.font = '600 24px monospace';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    Array.from(code).forEach((character, index) => {
        const slotWidth = canvas.width / code.length;
        context.save();
        context.translate(slotWidth * (index + 0.5), canvas.height / 2);
        context.rotate((Math.random() - 0.5) * 0.25);
        context.fillStyle = index % 2 === 0 ? '#1e293b' : '#334155';
        context.fillText(character, 0, 0);
        context.restore();
    });
}

/**
 * CaptchaField — self-hosted клиентская капча + honeypot.
 *
 * Использование:
 *   const captchaRef = useRef();
 *   <CaptchaField ref={captchaRef} />
 *   ...
 *   const { valid, isBot } = captchaRef.current.validate();
 *
 * Поведение:
 *  - `validate()` возвращает `{ valid, isBot }`:
 *      - `valid` — пользователь правильно ввёл текст с картинки;
 *      - `isBot` — true, если honeypot-поле заполнено (форма НЕ должна отправляться).
 *  - При неуспехе капча автоматически перегенерируется и поле очищается.
 */
const CaptchaField = React.forwardRef(function CaptchaField(
    { length = 5, className = '' },
    ref
) {
    const [value, setValue] = useState('');
    const [honeypot, setHoneypot] = useState('');
    const [error, setError] = useState('');
    const [code, setCode] = useState(() => createCaptchaCode(length));
    const canvasRef = useRef(null);

    const refresh = useCallback(() => {
        setValue('');
        setError('');
        setCode(createCaptchaCode(length));
    }, [length]);

    useEffect(() => {
        setCode(createCaptchaCode(length));
    }, [length]);

    useEffect(() => {
        if (canvasRef.current) drawCaptcha(canvasRef.current, code);
    }, [code]);

    React.useImperativeHandle(ref, () => ({
        /**
         * Проверить капчу. Возвращает { valid, isBot }.
         * При неуспехе автоматически обновляет картинку.
         */
        validate() {
            if (honeypot) {
                // Бот заполнил honeypot — молча отклоняем.
                refresh();
                return { valid: false, isBot: true };
            }
            const ok = value.trim().toUpperCase() === code;
            if (!ok) {
                setValue('');
                setCode(createCaptchaCode(length));
                setError('Неверный код. Попробуйте ещё раз.');
                return { valid: false, isBot: false };
            }
            setError('');
            return { valid: true, isBot: false };
        },
        reset: refresh,
    }));

    return (
        <div className={className}>
            <label className="block text-sm font-medium text-text-primary mb-2">
                Подтвердите, что вы не робот
            </label>

            <Flex align="center" gap={12} justify="start" className="mb-2">
                <div className="px-3 py-1 bg-secondary-50 border border-secondary-200 rounded-md select-none">
                    <canvas
                        ref={canvasRef}
                        width={150}
                        height={40}
                        role="img"
                        aria-label="Код подтверждения"
                        className="block h-10 w-[150px]"
                    />
                </div>
                <Button
                    type="text"
                    onClick={refresh}
                    className="p-2 "
                    title="Обновить код"
                    aria-label="Обновить код"
                    icon={<Icon name="RotateCcw" size={16} className="text-secondary-500" />}
                >
                </Button>
            </Flex>

            <Input
                type="text"
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    if (error) setError('');
                }}
                autoComplete="off"
                spellCheck={false}
                placeholder="Введите код с картинки"
                className={`
                    w-full px-4 py-2.5 
                    ${error
                        ? 'border-error focus:border-error'
                        : 'border-secondary-300 focus:border-primary'}
                `}
            />
            {error && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                    <Icon name="AlertCircle" size={14} />
                    {error}
                </p>
            )}

            {/* Honeypot — скрыт от пользователя, виден ботам. */}
            <Input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    left: '-10000px',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none',
                }}
            />
        </div>
    );
});

export default CaptchaField;
